<template>
  <a-modal
    :visible="visible"
    centered
    @cancel="handleCancel"
    width="70%"
    :footer="false"
  >
    <template slot="title">
      <div class="text-center">
        <h4 class="mb-0">{{ $t("bulletin.duplicateSettings") }}</h4>
      </div>
    </template>

    <div class="p-3">
      <!-- Source Section (read-only: comes from parametrage screen) -->
      <div class="mb-4">
        <a-divider orientation="left">{{ $t("bulletin.source") }}</a-divider>
        <div class="row g-3">
          <div class="col-md-12">
            <label class="form-label">{{ $t("conseilClass.selectSourceTemplate") }}</label>
            <a-input :value="sourceTemplateLabel" read-only disabled />
            <small class="text-muted d-block mt-1">
              {{ $t("conseilClass.cloneSourceHint") }}
            </small>
          </div>

          <div class="col-md-4">
            <label class="form-label">{{ $t("etablissement.type") }}</label>
            <a-input :value="sourceTypeLabel" read-only disabled />
          </div>

          <div class="col-md-4">
            <label class="form-label">{{ $t("etablissement.niveau") }}</label>
            <a-input :value="sourceLevelLabel" read-only disabled />
          </div>

          <div class="col-md-4">
            <label class="form-label">{{ $t("emploi.schoolarYear") }}</label>
            <a-input :value="sourceSchoolarYearLabel" read-only disabled />
          </div>
        </div>
      </div>

      <!-- Target Section -->
      <div class="mb-4">
        <a-divider orientation="left">{{ $t("bulletin.targets") }}</a-divider>
        <div class="row g-3">
          <div class="col-md-2">
            <a-button type="primary" class="w-100 mt-4" @click="selectAll">
              {{ $t("sms.selectAll") }}
            </a-button>
          </div>

          <div class="col-md-4">
            <label class="form-label">{{ $t("etablissement.type") }}</label>
            <a-select
              class="w-100"
              :value="targetTypes"
              mode="multiple"
              allow-clear
              @change="targetTypes = $event"
              :placeholder="$t('bulletin.selectTypes')"
            >
              <a-select-option
                v-for="(value, key) in builletinTypesNames"
                :key="key"
                :value="key"
              >
                {{ value }}
              </a-select-option>
            </a-select>
          </div>

          <div class="col-md-3">
            <label class="form-label">{{ $t("etablissement.niveau") }}</label>
            <a-select
              class="w-100"
              :value="targetLevels"
              mode="multiple"
              allow-clear
              @change="targetLevels = $event"
              :placeholder="$t('bulletin.selectLevels')"
            >
              <a-select-option
                v-for="(value, name, index) in levels"
                :key="index"
                :value="name"
              >
                {{ value }}
              </a-select-option>
            </a-select>
          </div>
          <div class="col-md-3">
            <label class="form-label">{{ $t("emploi.schoolarYear") }}</label>
            <a-select
              class="w-100"
              :value="targetSchoolarYears"
              mode="multiple"
              allow-clear
              @change="targetSchoolarYears = $event"
            >
              <a-select-option
                v-for="schoolarYear in settings.schoolarYears"
                :key="schoolarYear._id"
                :value="schoolarYear._id"
              >
                {{ schoolarYear.name }}
              </a-select-option>
            </a-select>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="row mt-4">
        <div class="col-md-4 offset-md-8">
          <a-button type="primary" class="w-100" @click="clone">
            <a-icon type="save" />
            {{ $t("all.apply") }}
          </a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script>
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import { isBuiltinTemplateId } from "./builtinTemplates";

export default {
  name: "SysGestLMSParamsClone",
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    levels: {
      type: Object,
      required: true,
    },
    builletinTypesNames: {
      type: Object,
      required: true,
    },
    currentLevel: {
      type: [String, Number],
      default: "",
    },
    currentType: {
      type: String,
      default: "MP",
    },
    currentTemplateId: {
      type: String,
      default: "",
    },
    currentTemplateLabel: {
      type: String,
      default: "",
    },
    currentSchoolarYear: {
      type: String,
      default: "",
    },
  },
  computed: {
    ...mapState(["settings", "user"]),
    sourceTemplateLabel() {
      return this.currentTemplateLabel || this.$t("conseilClass.unnamedTemplate");
    },
    sourceTypeLabel() {
      return this.builletinTypesNames[this.sourceType] || this.sourceType || "";
    },
    sourceLevelLabel() {
      return this.levels[String(this.sourceLevel)] || String(this.sourceLevel || "");
    },
    sourceSchoolarYearLabel() {
      const sy = this.settings.schoolarYears?.find(
        (item) => String(item._id) === String(this.sourceSchoolarYear)
      );
      return sy?.name || "";
    },
  },
  watch: {
    visible: {
      handler: async function (newVal) {
        if (!newVal) return;
        await apiClient
          .post("/bulletin/v2/conseil_class/filter", { listAll: true })
          .then(({ data }) => {
            this.configList = (data || []).filter((item) => !item.classRoom);
          })
          .catch(() => {
            this.configList = [];
          });
        this.initSourceFromContext();
      },
    },
  },
  data() {
    return {
      configList: [],
      sourceLevel: "",
      sourceType: "",
      sourceTemplateId: "",
      sourceSchoolarYear: "",
      targetLevels: [],
      targetTypes: [],
      targetSchoolarYears: [],
    };
  },
  methods: {
    handleCancel() {
      this.$emit("hide");
    },
    initSourceFromContext() {
      this.sourceSchoolarYear =
        this.currentSchoolarYear || this.settings.activeSchoolarYear;
      this.sourceLevel =
        this.currentLevel !== "" && this.currentLevel != null
          ? Number(this.currentLevel)
          : "";
      this.sourceType = this.currentType || "MP";

      if (
        this.currentTemplateId &&
        !isBuiltinTemplateId(this.currentTemplateId)
      ) {
        this.sourceTemplateId = this.currentTemplateId;
        return;
      }

      const assigned = this.configList.find(
        (template) =>
          Number(template.level) === Number(this.sourceLevel) &&
          template.type === this.sourceType &&
          String(template.schoolarYear?._id || template.schoolarYear) ===
            String(this.sourceSchoolarYear)
      );
      this.sourceTemplateId = assigned?._id || "";
    },
    selectAll() {
      this.targetTypes = ["MP", "MT", "MS"];
      this.targetLevels = Object.keys(this.levels);
      this.targetSchoolarYears = this.settings.schoolarYears.map(
        (sy) => sy._id
      );
    },
    clone() {
      if (
        !this.targetLevels.length ||
        !this.targetTypes.length ||
        !this.targetSchoolarYears.length
      ) {
        this.$message.warning(this.$t("conseilClass.selectCloneTargets"));
        return;
      }
      const payload = {
        targetLevels: this.targetLevels.map((level) => Number(level)),
        targetSchoolarYears: this.targetSchoolarYears,
        targetTypes: this.targetTypes,
      };
      if (this.sourceTemplateId) {
        payload.sourceTemplateId = this.sourceTemplateId;
      } else if (
        this.sourceLevel !== "" &&
        this.sourceType &&
        this.sourceSchoolarYear
      ) {
        payload.sourceLevel = Number(this.sourceLevel);
        payload.sourceType = this.sourceType;
        payload.sourceSchoolarYear = this.sourceSchoolarYear;
      } else {
        this.$message.warning(this.$t("conseilClass.cloneSaveTemplateFirst"));
        return;
      }
      apiClient
        .post("/bulletin/v2/conseil_class/clone", payload)
        .then(() => {
          this.$message.success(this.$t("bulletin.allDataDuplicatedSuccess"));
          this.$emit("cloneHappened");
        })
        .catch((e) => {
          console.info(e);
          this.$message.error(this.$t("conseilClass.checkInputs"));
        });
    },
  },
};
</script>
